import { useCallback, useEffect, type JSX } from "react";
import { FluentSpinner } from "@/components/fluent-ui";
import { HoursApi } from "@/services/api/hours-api";
import { useApi } from "@/hooks/useApi";
import { DealTracker } from "./DealTracker";
import { DealTrackerStaffer } from "./DealTrackerStaffer";
import { resolveDealTrackerRole, type DealTrackerRole } from "./deal-tracker-access";

export interface DealTrackerHomeProps {
  onClose?: () => void;
  /**
   * Reports the resolved role to a host that titles its own chrome — the Office
   * dialog header — so the role is fetched once rather than by both.
   */
  onRoleResolved?: (role: DealTrackerRole | null) => void;
}

/**
 * Routes to the Deal Tracker surface the signed-in user belongs on: their own
 * tracker for a Junior Banker (TAD-6037), the team list for a Staffer
 * (TAD-6072/6073).
 *
 * The role is resolved here rather than passed in because the Office dialog is a
 * separate document with its own session — it cannot inherit what the task pane
 * already worked out.
 */
export function DealTrackerHome({ onClose, onRoleResolved }: DealTrackerHomeProps): JSX.Element {
  const fetcher = useCallback(() => HoursApi.getUserSubDepartments(), []);
  const { data: subDepartments, loading, error, refetch } = useApi(fetcher);
  const role = subDepartments ? resolveDealTrackerRole(subDepartments) : null;

  useEffect(() => {
    // Only report a role the lookup actually answered. On failure `role` is
    // null, and announcing that would title the dialog as a Banker's.
    if (!loading && !error) onRoleResolved?.(role);
  }, [loading, error, role, onRoleResolved]);

  if (loading) {
    return (
      <div className="deal-tracker__state" role="status">
        <FluentSpinner label="Checking your TRIYO access…" />
      </div>
    );
  }

  // Without the answer there is no way to tell a Staffer from a Banker, and
  // guessing sends a Staffer to a surface that dead-ends at "No Deal Tracker".
  if (error) {
    return (
      <div className="deal-tracker__state" role="alert">
        <p>{error}</p>
        <button type="button" className="deal-tracker__secondary-button" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  return role === "staffer" ? <DealTrackerStaffer /> : <DealTracker onClose={onClose} />;
}
